
import { useState, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';

// Declaración de tipos para OneSignal
declare global {
  interface Window {
    OneSignal?: any;
    OneSignalDeferred?: any[];
  }
}

export const useOneSignal = () => {
  const { user } = useAuth();
  const [isInitialized, setIsInitialized] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasCheckedInitialState, setHasCheckedInitialState] = useState(false);

  // Función para verificar si OneSignal está disponible o se puede cargar
  const checkOneSignalAvailability = (): boolean => {
    // Verificar si ya está disponible
    if (window.OneSignal) {
      console.log('[OneSignal] SDK ya disponible');
      return true;
    }

    // Verificar si estamos en un contexto web válido
    if (typeof window === 'undefined') {
      console.log('[OneSignal] No estamos en un navegador');
      return false;
    }

    // Verificar si el navegador soporta notificaciones
    if (!('Notification' in window)) {
      console.log('[OneSignal] El navegador no soporta notificaciones');
      return false;
    }

    // Verificar si estamos en HTTPS o localhost
    const isSecure = window.location.protocol === 'https:' || 
                    window.location.hostname === 'localhost' ||
                    window.location.hostname === '127.0.0.1';
    
    if (!isSecure) {
      console.log('[OneSignal] Contexto no seguro - se requiere HTTPS');
      return false;
    }

    // Verificar si el Service Worker está soportado
    if (!('serviceWorker' in navigator)) {
      console.log('[OneSignal] Service Workers no soportados');
      return false;
    }

    // Si llegamos aquí, OneSignal debería poder cargarse
    console.log('[OneSignal] Contexto válido para OneSignal');
    return true;
  };

  const checkSubscriptionStatus = async () => {
    try {
      if (window.OneSignal) {
        const subscribed = await window.OneSignal.User.PushSubscription.optedIn;
        console.log('[OneSignal] Estado de suscripción verificado:', subscribed);
        setIsSubscribed(subscribed);
        return subscribed;
      }
      return false;
    } catch (error) {
      console.error('[OneSignal] Error verificando suscripción:', error);
      return false;
    }
  };

  const initializeOneSignal = async () => {
    console.log('[OneSignal] Iniciando inicialización...');

    // Verificar disponibilidad primero
    if (!checkOneSignalAvailability()) {
      console.log('[OneSignal] OneSignal no está disponible en este contexto');
      setHasCheckedInitialState(true);
      return;
    }

    // Si no hay usuario autenticado, marcar como verificado pero no inicializar
    if (!user?.id) {
      console.log('[OneSignal] No hay usuario autenticado');
      setHasCheckedInitialState(true);
      return;
    }

    try {
      console.log('[OneSignal] Inicializando para usuario autenticado:', user.id);
      
      // Si OneSignal ya está disponible
      if (window.OneSignal) {
        console.log('[OneSignal] OneSignal ya disponible, configurando...');
        setIsInitialized(true);
        
        // Configurar usuario
        try {
          await window.OneSignal.login(user.id);
          console.log('[OneSignal] Usuario configurado:', user.id);
        } catch (error) {
          console.error('[OneSignal] Error configurando usuario:', error);
        }

        // Verificar estado de suscripción
        await checkSubscriptionStatus();
        setHasCheckedInitialState(true);

        // Configurar listeners si no están ya configurados
        if (!window.OneSignal._listenersConfigured) {
          window.OneSignal.User.PushSubscription.addEventListener('change', (event: any) => {
            console.log('[OneSignal] Cambio en suscripción:', event);
            setIsSubscribed(event.current.optedIn);
          });
          window.OneSignal._listenersConfigured = true;
        }

        return;
      }

      // Si OneSignal no está disponible, intentar cargarlo
      console.log('[OneSignal] Cargando SDK...');
      
      // Crear el script tag si no existe
      if (!document.querySelector('script[src*="onesignal"]')) {
        const script = document.createElement('script');
        script.src = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
        
        console.log('[OneSignal] Script añadido al DOM');
      }

      // Usar el método diferido para inicializar
      window.OneSignalDeferred = window.OneSignalDeferred || [];
      window.OneSignalDeferred.push(async (OneSignal: any) => {
        console.log('[OneSignal] Inicializando SDK diferido...');
        
        await OneSignal.init({
          appId: "37736039-8ce6-4e98-8181-dadc0a0c6fa7",
          safari_web_id: "web.onesignal.auto.0a199198-d5df-41c5-963c-72a0258657aa",
          notifyButton: {
            enable: false,
          },
          promptOptions: {
            slidedown: {
              enabled: false,
            },
            customlink: {
              enabled: false,
            }
          },
          allowLocalhostAsSecureOrigin: true,
        });

        setIsInitialized(true);
        console.log('[OneSignal] Inicializado correctamente para usuario:', user.id);
        
        // Configurar usuario
        try {
          await OneSignal.login(user.id);
          console.log('[OneSignal] Usuario configurado (diferido):', user.id);
        } catch (error) {
          console.error('[OneSignal] Error configurando usuario (diferido):', error);
        }

        // Verificar estado de suscripción
        await checkSubscriptionStatus();
        setHasCheckedInitialState(true);

        // Configurar listeners
        OneSignal.User.PushSubscription.addEventListener('change', (event: any) => {
          console.log('[OneSignal] Cambio en suscripción (diferido):', event);
          setIsSubscribed(event.current.optedIn);
        });
      });

      // Timeout de seguridad
      setTimeout(() => {
        if (!hasCheckedInitialState) {
          console.log('[OneSignal] Timeout alcanzado, marcando como verificado');
          setHasCheckedInitialState(true);
          // Si después del timeout OneSignal está disponible, marcarlo como inicializado
          if (window.OneSignal) {
            setIsInitialized(true);
          }
        }
      }, 10000);
      
    } catch (error) {
      console.error('[OneSignal] Error en inicialización:', error);
      setHasCheckedInitialState(true);
    }
  };

  // Solo inicializar cuando hay usuario autenticado o al cargar la página
  useEffect(() => {
    initializeOneSignal();
  }, [user?.id]);

  const requestPermission = async (): Promise<boolean> => {
    if (!checkOneSignalAvailability()) {
      console.error('[OneSignal] OneSignal no está disponible');
      toast.error('Las notificaciones no están disponibles en este navegador');
      return false;
    }

    if (!window.OneSignal) {
      console.error('[OneSignal] OneSignal SDK no está cargado');
      toast.error('OneSignal no está inicializado');
      return false;
    }

    setIsLoading(true);
    
    try {
      console.log('[OneSignal] Solicitando permisos...');
      
      // Usar la API nativa del navegador para pedir permisos
      const permission = await Notification.requestPermission();
      console.log('[OneSignal] Respuesta del navegador:', permission);
      
      if (permission === 'granted') {
        // Una vez concedidos los permisos, suscribir al usuario
        await window.OneSignal.User.PushSubscription.optIn();
        
        // Verificar suscripción
        const subscribed = await checkSubscriptionStatus();
        
        if (subscribed) {
          toast.success('¡Notificaciones activadas correctamente!');
          
          // Configurar usuario
          if (user?.id) {
            try {
              await window.OneSignal.login(user.id);
              console.log('[OneSignal] Usuario configurado después de suscripción:', user.id);
            } catch (error) {
              console.error('[OneSignal] Error configurando usuario:', error);
            }
          }
        } else {
          // Reintentar después de un delay
          setTimeout(async () => {
            const retrySubscribed = await checkSubscriptionStatus();
            if (retrySubscribed) {
              toast.success('¡Notificaciones activadas correctamente!');
            } else {
              toast.error('Error al activar notificaciones');
            }
          }, 1000);
        }
        
        return true;
      } else {
        toast.error('Permisos de notificación denegados');
        return false;
      }
      
    } catch (error) {
      console.error('[OneSignal] Error solicitando permisos:', error);
      toast.error('Error al solicitar permisos de notificación');
      return false;
    } finally {
      setIsLoading(false);
    }
  };

  const unsubscribe = async (): Promise<void> => {
    if (!window.OneSignal) {
      console.error('[OneSignal] OneSignal no está disponible para desuscribir');
      return;
    }
    
    setIsLoading(true);
    
    try {
      console.log('[OneSignal] Desuscribiendo...');
      await window.OneSignal.User.PushSubscription.optOut();
      
      // Verificar estado
      const subscribed = await checkSubscriptionStatus();
      
      if (!subscribed) {
        toast.success('Notificaciones desactivadas');
      } else {
        // Reintentar
        setTimeout(async () => {
          const retrySubscribed = await checkSubscriptionStatus();
          if (!retrySubscribed) {
            toast.success('Notificaciones desactivadas');
          }
        }, 1000);
      }
    } catch (error) {
      console.error('[OneSignal] Error desuscribiendo:', error);
      toast.error('Error al desactivar notificaciones');
    } finally {
      setIsLoading(false);
    }
  };

  return {
    isInitialized: isInitialized && checkOneSignalAvailability(),
    isSubscribed,
    isLoading,
    hasCheckedInitialState,
    requestPermission,
    unsubscribe
  };
};
